import Image from 'next/image';
import styles from './ProductDetailBox.module.css';
import { useEffect, useLayoutEffect, useState } from 'react';
import { IProductData } from '@/interfaces';
import { useGlobalContext } from '@/lib/context';
import { WishlistType } from '@/lib/constants';
import { WishlistIcon } from '@/components/shared/wishlistIcon';
import { marketPlaceLoader } from '@/lib/helpers/imageLoader';
import { formatToIndianCurrency, pushRoute } from '@/lib/helpers/common';
import Loader from '@/components/shared/loader/Loader';
import { Input as InputText } from '../../../../components/ui/input';
import { API } from "@/lib/helpers/http-client";
import { useToast } from '@/components/shared/toast/ToastContainer';
import { getCookie } from '@/lib/helpers/Cookie';
import { OfferBox } from './OfferBox';


type props = {
    data: IProductData
}

export function ProductDetailBox({ data }: props) {
    const { props: { addToWishlist, wishlistItems, isMobileView, addToCart, setLoading, isAppView } } = useGlobalContext();
    const productDetail = data.productDetail || {};
    const [productImages] = useState(productDetail.files || []);
    const [activeImage, setActiveImage] = useState(0);
    const [detailTable, setDetailTable] = useState(true);
    const siteURL = process.env.SITE_URL;
    const [showLinks, setShowLinks] = useState(false);
    const facebook = `https://www.facebook.com/sharer/sharer.php?u=`;
    const whatsApp = `whatsapp://send?text=`;
    const [show3dPopUp, setShow3dPopup] = useState(false);
    const [loader, setLoader] = useState(false);
    const [isActivateVr, setActivateVr] = useState(false);
    const [isModelLoaded, setModelLoaded] = useState(false);
    const [pincode, setPincode] = useState<string>('');
    const [pincodeVerified, setPincodeVerified] = useState(false);
    const [cartProduct, setCartProduct] = useState(data.isProductIsIncart);
    const [validPincode, setPincodeValid] = useState(false)
    const [productPrice, setProductPrice] = useState({
        mrp: productDetail.mrp,
        offerPrice: productDetail.offerPrice,
        discountPercent: productDetail.discountPercent,
        availabilityStatus: productDetail.availabilityStatus,
        deliveryEstimate: ""
    });
    const [pincodeError, setPincodeError] = useState('');
    const addToast = useToast();
    const activateAr = () => {
        if (isActivateVr) {
            const modelViewer = document.querySelector('model-viewer');
            if (modelViewer) {
                modelViewer.activateAR();
            }
        }
    };
    const show3dModel = (activateVr: boolean) => {
        setLoader(true);
        import('@google/model-viewer');
        show3dPopUp && document.body.classList.add('overflow-hidden');
        const modelViewer = document.createElement("model-viewer");
        modelViewer.src = data.productDetail.threeDFile;
        modelViewer.setAttribute('ar', '');
        modelViewer.setAttribute('shadow-intensity', '1');
        modelViewer.setAttribute('disable-tap', '');
        modelViewer.setAttribute('camera-controls', '');
        modelViewer.setAttribute('touch-action', 'pan-y');
        modelViewer.setAttribute('loading', 'eager');
        const modelviewerDiv: any = document.getElementById('modelviewer');
        modelviewerDiv.innerHTML = '';
        modelviewerDiv.appendChild(modelViewer);
        modelViewer.addEventListener('load', () => {
            setLoader(false);
            setModelLoaded(true);
            if (activateVr) {
                setActivateVr(true);
            }
        });
    };
    useEffect(() => {
        if (isModelLoaded && isActivateVr) {
            activateAr();
        }
    }, [isModelLoaded, isActivateVr]);
    useEffect(() => {
        const handleClick = (event: any) => {
            if (event.target.id !== 'shareLinks') {
                setShowLinks(false);
            }
        };
        document.addEventListener('click', handleClick);
        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, [showLinks]);
    const handlePinCodeChange = (e: any) => {
        setPincodeError('');
        const value = e.target.value;
        const regex = /^[0-9]{0,6}$/;
        if (regex.test(value)) {
            setPincode(value);
        }
    };
    const handleCart = async (productId: number) => {
        if (cartProduct) {
            pushRoute(`${siteURL}/checkout/cart`)
        } else {
            setLoading(true)
            await addToCart([{
                productId: productId,
                quantity: 1,
                isProductSelected: true
            }]);
            setCartProduct(true);
            setLoading(false);
            addToast('Item sucessfully added to cart!', 'success');
        }
    }
    const checkPincode = async () => {
        if (pincode.length !== 6) {
            setPincodeError('Please enter a valid pincode');
            return;
        }

        try {
            if (pincodeVerified) {
                setPincodeVerified(false);
                setPincodeValid(false)
                setPincodeError('');
                setPincode('');
            } else {
                const response = await API.post(`/api/checkproductavailability?productId=${data.productId}&pincode=${pincode}`);
                const pincodeProductData = response.data;
                if (Object.keys(pincodeProductData).length === 0) {
                    setPincodeError('Sorry, we do not ship to your pincode');
                }
                else if(pincodeProductData.availabilityStatus == 0){
                    setPincodeError('Sorry, this product is currently out of stock');
                } else {
                    setPincodeError('');
                    setProductPrice({
                        mrp: pincodeProductData.mrp,
                        offerPrice: pincodeProductData.offerPrice,
                        discountPercent: pincodeProductData.discountPercent,
                        availabilityStatus: pincodeProductData.availabilityStatus,
                        deliveryEstimate: pincodeProductData.estimatedDeliveryDate
                    });
                    setPincodeValid(true)
                }
                setPincodeVerified(true);
            }
        } catch (error) {
            console.error('Error fetching product availability:', error);
            setPincodeError('Error fetching product availability');
        }
    };
    useEffect(() => {
        const cartItems = JSON.parse(getCookie('cart') || "[]");
        cartItems.find((f: { productId: number; }) => f.productId === data.productId) ? setCartProduct(true) : null;
    }, []);
    return (
        <div className={styles.productDetailBox}>
            <div className={`${styles.containerFull} containerFull`}>
                <div className={styles.detailDesign}>
                    <div className={styles.leftImgBox}>
                        {(isMobileView && loader) && <Loader />}
                        <ul className={styles.nextDesignBox}>
                            {productImages.map((f, idx) => (
                                <li key={idx} onClick={() => setActiveImage(idx)}>
                                    <Image className="img-responsive" loading={isMobileView ? 'lazy' : 'eager'} src={f.filePath} alt="" loader={marketPlaceLoader} width={88} height={88} />
                                </li>
                            ))}
                        </ul>
                        <div className={styles.productImgBox}>
                            <figure>
                                <Image className="img-responsive" src={productImages[activeImage].filePath} alt="" loading='eager' loader={marketPlaceLoader} width={536} height={476} />
                            </figure>
                            <WishlistIcon WishlistType={WishlistType.CATALOGUE} productId={data.productId} />
                            {productImages.length > 1 && (
                                <div className={styles.arrowFlowBox}>
                                    <span className={styles.arrowBtn} onClick={() => setActiveImage(activeImage === 0 ? 0 : activeImage - 1)}>
                                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 1 1 7l6 6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                        </svg>
                                    </span>
                                    <span className={styles.arrowBtn} onClick={() => setActiveImage(activeImage === productImages.length - 1 ? productImages.length - 1 : activeImage + 1)}>
                                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="m1 13 6-6-6-6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                        </svg>
                                    </span>
                                </div>
                            )}
                            {
                                (data.productDetail.threeDFile && !isAppView) &&
                                <div className={styles.viewMainBox}>
                                    <div className={`${styles.tileParent} ${styles.threeD}`}>
                                        <div className={styles.viewTileBox} onClick={() => (setModelLoaded(false), setActivateVr(false), show3dModel(false), setShow3dPopup(true))}>
                                            <Image src="/assets/images/marketplace/viewIn3d.svg" alt="location" width={24} height={20} />
                                            <span className={styles.titileCase}>View in 3D</span>
                                        </div>
                                        <div className={`${styles.overlay} overlay ${!show3dPopUp && 'displayNone'}`}>
                                            {loader && <Loader />}
                                            <div className={styles.popupMainBox}>
                                                <div className={styles.topHeader}>
                                                    <div className={styles.viewInThrDBtn}>
                                                        <Image src="/assets/images/marketplace/viewIn3dWhite.svg" alt="location" width={24} height={20} />
                                                        <span className={styles.titileCase}>3D View</span>
                                                    </div>
                                                    <div className={styles.popUpclose} onClick={() => (setShow3dPopup(false), document.body.classList.remove('overflow-hidden'))}></div>
                                                </div>
                                                <div className={styles.popUpContentBox}>
                                                    <div className={styles.rightCntBox}>
                                                        <p className='font16 text222 f600 mt10'>{data.productName}</p>
                                                    </div>
                                                    <div className={styles.leftThreDBox} id='modelviewer'>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`${styles.tileParent} ${styles.room}`}>
                                        <div className={`${styles.viewTileBox} ${styles.room}`} onClick={() => (setModelLoaded(false), setActivateVr(false), show3dModel(true))}>
                                            <Image src="/assets/images/marketplace/viewInRoom.svg" alt="location" width={24} height={20} />
                                            <span className={styles.titileCase}>View in Room</span>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                    <div className={styles.rightcntBox}>
                        <h1 className="font24 text000 f600 lineHeight32">{data.productName}</h1>
                        <div className={styles.priceDetail}>
                            <span className={`${styles.price} text222 font26 f600`}>₹ {formatToIndianCurrency(productPrice.offerPrice)}</span>
                            {productPrice.offerPrice !== productPrice.mrp && (
                                <>
                                    <span className={`${styles.orgPrice} text666 font20`}>₹ {formatToIndianCurrency(productPrice.mrp)}</span>
                                    <span className={`${styles.off} f600 textpink font20`}>{productPrice.discountPercent}% Off</span>
                                </>
                            )}
                        </div>
                        <span className={`${styles.inTax} text222 font16`}>Inclusive of all taxes</span>
                        <OfferBox payUOfferList={data.payUOfferList}/>
                        {productPrice.availabilityStatus === 0 && <span className={`${styles.inTax} ${styles.outOfStock} textred font16`}>This product is currently sold out</span>}
                        <div className={styles.ctaBtns}>
                            <button className={`${styles.wishListBtn} ${styles.btn} ${productPrice.availabilityStatus === 0 && styles.disabled} btn`} onClick={() => { productPrice.availabilityStatus === 0 ? null : handleCart(data.productId) }}>
                                {
                                    productPrice.availabilityStatus === 0 ? 'Out of Stock' :
                                        <>
                                            
                                                <em>
                                                    <Image src="/assets/images/marketplace/cart.svg" alt="cart" width={18} height={16} />
                                                </em>
                                            {cartProduct ? 'Go To Cart' : 'Add To Cart'}
                                        </>
                                }
                            </button>
                            <button className={`${styles.shareLink} ${styles.btn} btn`} onClick={() => addToWishlist(data.productId, WishlistType.CATALOGUE)}>
                                <em><Image width={18} height={20} src={`/assets/images/marketplace/${wishlistItems && wishlistItems.find(item => item.prodId === data.productId && item.type === WishlistType.CATALOGUE) ? "heart-red-fill" : "black-border-heart"}.svg`} alt="share" /></em>
                            </button>
                            <button id='shareLinks' className={`${styles.shareLink} ${showLinks && styles.active} ${styles.btn} btn`} onClick={() => setShowLinks(!showLinks)}>
                                <em>
                                    <Image id='shareLinks' width={18} height={20} src="/assets/images/share.svg" alt="share" />
                                </em>
                                <div className={`${styles.sharePopmainbx}`} id='shareLinks'>
                                    <span className="dIlineBlock mb16 font16 text222 f500 shareHead">Share This Design</span>
                                    <ul>
                                        <li onClick={() => window.open(`${facebook}${siteURL}/online-store/${data.productSlug}`, '_blank')}>
                                            <Image width={18} height={18} className="img-responsive" src="/assets/images/Fasebooknewlogo.svg" alt="Fasebooknewlogo" />
                                            <span className="font14 f500 facebookColur fontSM12">Facebook</span>
                                        </li>
                                        <li onClick={() => window.open(`${whatsApp}${siteURL}/online-store/${data.productSlug}`, '_blank')}>
                                            <Image width={18} height={18} className="img-responsive" src="/assets/images/whatsapplogonew.svg" alt="whatsapplogonew" />
                                            <span className="font14 f500 whatsappColur fontSM12">Whatsapp</span>
                                        </li>
                                        <li onClick={() => { navigator.clipboard.writeText(`${siteURL}/online-store/${data.productSlug}`) }}>
                                            <Image width={16} height={18} className="img-responsive" src="/assets/images/cpoylinklogo.svg" alt="cpoylinklogo" />
                                            <span className="font14 f500 copylinkColur fontSM12">Copy Link</span>
                                        </li>
                                    </ul>
                                </div>
                            </button>
                        </div>
                        <div className={styles.deliveryPinBox}>
                            <p className={styles.heading}>Delivery & Assembly</p>
                            <div className={styles.inputPinBox}>
                                <InputText type="tel" autoComplete='off' disabled={pincodeVerified} className="formControl" placeholder='Enter Pincode' value={pincode} onChange={handlePinCodeChange} maxLength={6} required={true} />
                                <div className={styles.pinTickMain}>
                                    {validPincode && <div className={styles.tickBox}></div>}
                                    <span
                                        className={`${styles.pinCheckBtn}`}
                                        onClick={checkPincode}
                                    >
                                        {pincodeVerified ? 'Change' : 'Check'}
                                    </span>
                                </div>
                            </div>
                            {validPincode && <span className={`${styles.successText}`}>Get it by {productPrice.deliveryEstimate}</span>}
                            {pincodeError && <span className={`${styles.errorText}`}>{pincodeError}</span>}
                        </div>
                        <div className={styles.gaurentyMainbox}>
                            <div className={styles.guatunMiancards}>
                                <Image width={50} height={50} className={`img-responsive ${styles.hundtaPerspr}`} src="/assets/images/marketplace/premQuality.svg" alt="guarantee" />
                                <span className="font12 text000 lineHeight10 fontSM10">100% Premium Quality</span>
                            </div>
                            <div className={styles.guatunMiancards}>
                                <Image width={67} height={50} className={`img-responsive ${styles.esyInstimg}`} src="/assets/images/marketplace/easyInstall.svg" alt="guarantee" />
                                <span className="font12 text000 lineHeight10 fontSM10">Easy Installation</span>
                            </div>
                            <div className={styles.guatunMiancards}>
                                <Image width={51} height={50} className={`img-responsive ${styles.hundtaPerspr}`} src="/assets/images/marketplace/freeDelivery.svg" alt="guarantee" />
                                <span className="font12 text000 lineHeight10 fontSM10">Free & Fast Delivery</span>
                            </div>
                            <div className={styles.guatunMiancards}>
                                <Image width={61} height={50} className={`img-responsive ${styles.hundtaPerspr}`} src="/assets/images/marketplace/10YrsWarrnty.svg" alt="guarantee" />
                                <span className="font12 text000 lineHeight10 fontSM10">1 Year Warranty</span>
                            </div>
                        </div>
                        <div className={detailTable ? `${styles.detailTableBox} ${styles.active}` : `${styles.detailTableBox}`} onClick={() => setDetailTable(!detailTable)}>
                            <h2 className="text222 font16 f600">Product Detail</h2>
                            <ul className={styles.detailList}>
                                {productDetail.attributes && productDetail.attributes.map((f, idx) => (
                                    <li key={idx}>
                                        <span>{f.attributeName}</span>
                                        <label>{f.attributeValue}</label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
